import React, { useCallback, useEffect, useRef, useState } from 'react';

/**
 * Horizontal scroller with a scrollbar you can actually see and drag.
 *
 * Mobile browsers hide native scrollbars until you are already scrolling,
 * so a wide table reads as "cut off" rather than "swipe me". This renders
 * its own bar, sticky to the bottom of the visible area, which works the
 * same with a finger as with a mouse.
 */
export const ScrollX = ({ children, className = '', testId, barClassName = '' }) => {
  const ref = useRef(null);
  const dragRef = useRef(null);
  const [geom, setGeom] = useState({ ratio: 1, pos: 0 });

  const measure = useCallback(() => {
    const el = ref.current;
    if (!el) return;
    const ratio = el.scrollWidth ? el.clientWidth / el.scrollWidth : 1;
    const max = el.scrollWidth - el.clientWidth;
    setGeom({ ratio, pos: max > 0 ? el.scrollLeft / max : 0 });
  }, []);

  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    if (el.firstElementChild) ro.observe(el.firstElementChild);
    el.addEventListener('scroll', measure, { passive: true });
    return () => {
      ro.disconnect();
      el.removeEventListener('scroll', measure);
    };
  }, [measure]);

  const thumbPct = Math.max(geom.ratio * 100, 14);

  const scrollTo = (clientX, trackRect) => {
    const el = ref.current;
    if (!el) return;
    const thumbW = (trackRect.width * thumbPct) / 100;
    const span = trackRect.width - thumbW;
    const max = el.scrollWidth - el.clientWidth;
    const x = clientX - trackRect.left - thumbW / 2;
    el.scrollLeft = span > 0 ? Math.max(0, Math.min(1, x / span)) * max : 0;
  };

  const onPointerDown = (e) => {
    const track = e.currentTarget;
    const rect = track.getBoundingClientRect();
    scrollTo(e.clientX, rect);
    dragRef.current = rect;
    try { track.setPointerCapture(e.pointerId); } catch { /* older browsers */ }
  };
  const onPointerMove = (e) => {
    if (dragRef.current) scrollTo(e.clientX, dragRef.current);
  };
  const onPointerUp = (e) => {
    dragRef.current = null;
    try { e.currentTarget.releasePointerCapture(e.pointerId); } catch { /* already released */ }
  };

  return (
    <div className="relative min-w-0">
      <div
        ref={ref}
        className={`overflow-x-auto overscroll-x-contain ${className}`}
        data-testid={testId}
      >
        {children}
      </div>
      {geom.ratio < 0.995 && (
        <div className={`sticky bottom-0 z-20 mt-1 pb-0.5 ${barClassName}`}>
          <div
            className="h-3.5 w-full cursor-pointer touch-none rounded-full bg-gray-200/95 shadow-inner"
            onPointerDown={onPointerDown}
            onPointerMove={onPointerMove}
            onPointerUp={onPointerUp}
            onPointerCancel={onPointerUp}
            data-testid={testId ? `${testId}-bar` : undefined}
          >
            <div
              className="h-3.5 rounded-full bg-[#AA0000]/75 transition-[margin] duration-75"
              style={{ width: `${thumbPct}%`, marginLeft: `${geom.pos * (100 - thumbPct)}%` }}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default ScrollX;
